import { DASH_PLACEHOLDER } from "../../constants/dashPlaceholder";
import type { ZipCodeItem } from "../../hooks/react-query/backOffice/transitTimesAndServiceLevels/useZipCodesList";

// Mock zip codes data for Playwright tests and MSW handlers
export const mockZipCodesByServiceCenter: Record<string, ZipCodeItem[]> = {
  "1": [
    {
      id: 1,
      zipCode: "30301",
      city: "Atlanta",
      state: "GA",
      otherServiceCenter: "Vendor A",
      alternativeVendor: DASH_PLACEHOLDER,
      dowService: [1, 2, 3, 4, 5], // Mon-Fri
      additionalDays: 1,
      effectiveDate: "2024-01-01",
      expiryDate: "2099-12-31",
      status: "Active",
      operation: "All",
    },
    {
      id: 2,
      zipCode: "30302",
      city: "Atlanta",
      state: "GA",
      otherServiceCenter: "Vendor A",
      alternativeVendor: "FEDX",
      dowService: [1, 2, 3, 4, 5],
      additionalDays: 0,
      effectiveDate: "2024-01-01",
      expiryDate: "2099-12-31",
      status: "Active",
      operation: "All",
    },
    {
      id: 3,
      zipCode: "30303",
      city: "Atlanta",
      state: "GA",
      otherServiceCenter: "Vendor B",
      alternativeVendor: DASH_PLACEHOLDER,
      dowService: [1, 2, 3, 4, 5],
      additionalDays: 0,
      effectiveDate: "2024-02-01",
      expiryDate: "2099-12-31",
      status: "Active",
      operation: "Delivery Only",
    },
    {
      id: 4,
      zipCode: "30304",
      city: "Atlanta",
      state: "GA",
      otherServiceCenter: "Vendor A",
      alternativeVendor: "SAIA",
      dowService: [1, 2, 3, 4, 5],
      additionalDays: 1,
      effectiveDate: "2024-03-01",
      expiryDate: "2099-12-31",
      status: "Active",
      operation: "Pickup Only",
    },
    {
      id: 5,
      zipCode: "30305",
      city: "Atlanta",
      state: "GA",
      otherServiceCenter: "Vendor C",
      alternativeVendor: DASH_PLACEHOLDER,
      dowService: [1, 2, 3, 4, 5],
      additionalDays: 0,
      effectiveDate: "2024-01-01",
      expiryDate: "2099-12-31",
      status: "Active",
      operation: "All",
    },
    {
      id: 6,
      zipCode: "30306",
      city: "Atlanta",
      state: "GA",
      otherServiceCenter: "Vendor B",
      alternativeVendor: "UPSG",
      dowService: [1, 2, 3, 4, 5],
      additionalDays: 0,
      effectiveDate: "2023-01-01",
      expiryDate: "2024-12-31",
      status: "Inactive",
      operation: "Delivery Only",
    },
  ],
  "2": [
    {
      id: 7,
      zipCode: "75201",
      city: "Dallas",
      state: "TX",
      otherServiceCenter: "Vendor A",
      alternativeVendor: DASH_PLACEHOLDER,
      dowService: [1, 2, 3, 4, 5],
      additionalDays: 1,
      effectiveDate: "2024-01-01",
      expiryDate: "2099-12-31",
      status: "Active",
      operation: "All",
    },
  ],
};

export type EolServiceCenterDetails = {
  id: string;
  scCode: string;
  name: string;
  operationType: string;
  vendorScac: string;
  status: "ACTIVE" | "INACTIVE";
  lhJunction: boolean;
  operationalStatus: string;
  operationalStartDate: string;
  operationalEndDate: string;
  physicalAddress: string;
  country: string;
  scEffectiveDate: string;
  scExpiryDate: string;
  linehaulJunction: string;
  addressEffectiveDate: string;
  addressExpiryDate: string;
  latitude: string;
  longitude: string;
  timezone: string;
};

/** EOL Service Center row for list/grid (UI format) */
type EolServiceCenterRowUI = {
  id: string;
  scCode: string;
  name: string;
  operationType: string;
  vendorScac: string;
  status: "ACTIVE" | "INACTIVE";
  lhJunction: boolean;
  latitude?: number;
  longitude?: number;
  timezone?: string;
};

/** EOL Service Centers for list page (matches detail IDs) */
export const mockEolServiceCenters: EolServiceCenterRowUI[] = [
  {
    id: "1",
    scCode: "ATL",
    name: "Atlanta EOL",
    operationType: "DYLT-operated",
    vendorScac: "-",
    status: "ACTIVE",
    lhJunction: true,
  },
  {
    id: "2",
    scCode: "DFW",
    name: "Dallas EOL",
    operationType: "DYLT-operated",
    vendorScac: "-",
    status: "ACTIVE",
    lhJunction: true,
  },
  {
    id: "3",
    scCode: "ORD",
    name: "Chicago EOL",
    operationType: "DYLT-operated",
    vendorScac: "-",
    status: "INACTIVE",
    lhJunction: false,
  },
  {
    id: "4",
    scCode: "LAX",
    name: "Los Angeles EOL",
    operationType: "Vendor-operated",
    vendorScac: "VDSC",
    status: "INACTIVE",
    lhJunction: false,
  },
  {
    id: "5",
    scCode: "EWR",
    name: "Newark EOL",
    operationType: "DYLT-operated",
    vendorScac: "-",
    status: "ACTIVE",
    lhJunction: true,
  },
];

export const mockEolServiceCenterDetails: Record<
  string,
  EolServiceCenterDetails | undefined
> = {
  "1": {
    id: "1",
    scCode: "ATL",
    name: "Atlanta EOL Service Center",
    operationType: "DYLT-operated",
    vendorScac: "-",
    status: "ACTIVE",
    lhJunction: true,
    operationalStatus: "Operational — No issues reported",
    operationalStartDate: "2023-01-01",
    operationalEndDate: "2025-12-31",
    physicalAddress: "5678 Peachtree Rd, Atlanta, GA 30308",
    country: "USA",
    scEffectiveDate: "2023-01-01",
    scExpiryDate: "2025-12-31",
    linehaulJunction: "ATL-HUB",
    addressEffectiveDate: "2023-01-01",
    addressExpiryDate: "2025-12-31",
    latitude: "33.7490°N",
    longitude: "84.3880°W",
    timezone: "America/New_York",
  },
  "2": {
    id: "2",
    scCode: "DFW",
    name: "Dallas EOL Service Center",
    operationType: "DYLT-operated",
    vendorScac: "N/A",
    status: "ACTIVE",
    lhJunction: true,
    operationalStatus: "Operational — No issues reported",
    operationalStartDate: "2023-01-01",
    operationalEndDate: "2025-12-31",
    physicalAddress: "1234 Commerce St, Dallas, TX 75201",
    country: "USA",
    scEffectiveDate: "2023-01-01",
    scExpiryDate: "2025-12-31",
    linehaulJunction: "N/A",
    addressEffectiveDate: "2023-01-01",
    addressExpiryDate: "2025-12-31",
    latitude: "32.7767°N",
    longitude: "96.7970°W",
    timezone: "America/Chicago",
  },
  "3": {
    id: "3",
    scCode: "ORD",
    name: "Chicago EOL Service Center",
    operationType: "DYLT-operated",
    vendorScac: "-",
    status: "INACTIVE",
    lhJunction: false,
    operationalStatus: "Maintenance — Temporarily offline",
    operationalStartDate: "2022-01-01",
    operationalEndDate: "2024-06-30",
    physicalAddress: "9876 Industrial Pkwy, Chicago, IL 60601",
    country: "USA",
    scEffectiveDate: "2022-01-01",
    scExpiryDate: "2024-06-30",
    linehaulJunction: "CHI-HUB",
    addressEffectiveDate: "2022-01-01",
    addressExpiryDate: "2024-06-30",
    latitude: "41.8781°N",
    longitude: "87.6298°W",
    timezone: "America/Chicago",
  },
  "4": {
    id: "4",
    scCode: "LAX",
    name: "Los Angeles EOL Service Center",
    operationType: "Vendor-operated",
    vendorScac: "VDSC",
    status: "INACTIVE",
    lhJunction: false,
    operationalStatus: "Inactive — Contract ended",
    operationalStartDate: "2021-01-01",
    operationalEndDate: "2023-12-31",
    physicalAddress: "2345 Harbor Blvd, Los Angeles, CA 90001",
    country: "USA",
    scEffectiveDate: "2021-01-01",
    scExpiryDate: "2023-12-31",
    linehaulJunction: "LAX-PORT",
    addressEffectiveDate: "2021-01-01",
    addressExpiryDate: "2023-12-31",
    latitude: "34.0522°N",
    longitude: "118.2437°W",
    timezone: "America/Los_Angeles",
  },
  "5": {
    id: "5",
    scCode: "EWR",
    name: "Newark EOL Service Center",
    operationType: "DYLT-operated",
    vendorScac: "-",
    status: "ACTIVE",
    lhJunction: true,
    operationalStatus: "Operational — No issues reported",
    operationalStartDate: "2023-01-01",
    operationalEndDate: "2025-12-31",
    physicalAddress: "4567 Airport Rd, Newark, NJ 07114",
    country: "USA",
    scEffectiveDate: "2023-01-01",
    scExpiryDate: "2025-12-31",
    linehaulJunction: "EWR-AIRPORT",
    addressEffectiveDate: "2023-01-01",
    addressExpiryDate: "2025-12-31",
    latitude: "40.7357°N",
    longitude: "74.1724°W",
    timezone: "America/New_York",
  },
};

/** --- EOL Details tab form options (mock until APIs provide these lists) --- */

/** Country dropdown values for the service center Details edit form. */
export const EOL_DETAILS_COUNTRY_OPTIONS: readonly string[] = [
  "USA",
  "Canada",
  "Mexico",
];

/** --- Add Service Center dialog (mock form options, addresses, duplicate rules) --- */

export const mockAddServiceCenterCountries = [
  "United States (USA)",
  "Canada (CAN)",
  "Mexico (MEX)",
] as const;

export type AddServiceCenterCountry =
  (typeof mockAddServiceCenterCountries)[number];

export const mockAddServiceCenterOperationTypes = [
  "DYLT-operated",
  "Vendor-operated",
] as const;

export type AddServiceCenterOperationType =
  (typeof mockAddServiceCenterOperationTypes)[number];

/**
 * Codes treated as “already taken” in Add Service Center (in addition to list SC codes).
 * Note: do not add the SC code used in the save-path acceptance criteria to this list;
 * duplicate checks use the live grid plus these reserved codes (e.g. DCHI/DATL).
 */
export const mockExistingScCodesForDuplicateCheck = ["DCHI", "DATL"] as const;

export type VendorScacOption = {
  code: string;
  label: string;
};

export const mockVendorScacOptions: readonly VendorScacOption[] = [
  { code: "SAIA", label: "Saia Motor Freight" },
  { code: "FXFE", label: "FedEx Freight" },
  { code: "UPSS", label: "UPS Supply Chain" },
] as const;

export type MockPhysicalAddress = {
  id: string;
  /** Full line shown in autocomplete and Physical Address field after select */
  fullAddress: string;
  street: string;
  suite: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
  timezone: string;
};

export const mockPhysicalAddresses: readonly MockPhysicalAddress[] = [
  {
    id: "addr-1",
    fullAddress: "1234 Commerce St, Dallas, TX 75201",
    street: "1234 Commerce St",
    suite: "",
    city: "Dallas",
    state: "TX",
    zip: "75201",
    latitude: 32.7767,
    longitude: -96.797,
    timezone: "America/Chicago",
  },
  {
    id: "addr-2",
    fullAddress: "231 Elm Street, Apt 12, Seattle, WA 98101",
    street: "231 Elm Street",
    suite: "Apt 12",
    city: "Seattle",
    state: "WA",
    zip: "98101",
    latitude: 47.6062,
    longitude: -122.3321,
    timezone: "America/Los_Angeles",
  },
  {
    id: "addr-3",
    fullAddress: "213 Oak Avenue, Suite 300, Denver, CO 80202",
    street: "213 Oak Avenue",
    suite: "Suite 300",
    city: "Denver",
    state: "CO",
    zip: "80202",
    latitude: 39.7392,
    longitude: -104.9903,
    timezone: "America/Denver",
  },
  {
    id: "addr-4",
    fullAddress: "321 Maple Drive, Floor 1, Miami, FL 33101",
    street: "321 Maple Drive",
    suite: "Floor 1",
    city: "Miami",
    state: "FL",
    zip: "33101",
    latitude: 25.7617,
    longitude: -80.1918,
    timezone: "America/New_York",
  },
  {
    id: "addr-5",
    fullAddress: "500 Market St, Suite 100, San Francisco, CA 94105",
    street: "500 Market St",
    suite: "Suite 100",
    city: "San Francisco",
    state: "CA",
    zip: "94105",
    latitude: 37.7749,
    longitude: -122.4194,
    timezone: "America/Los_Angeles",
  },
  {
    id: "addr-6",
    fullAddress: "88 Peachtree St NE, Floor 12, Atlanta, GA 30303",
    street: "88 Peachtree St NE",
    suite: "Floor 12",
    city: "Atlanta",
    state: "GA",
    zip: "30303",
    latitude: 33.749,
    longitude: -84.388,
    timezone: "America/New_York",
  },
  {
    id: "addr-7",
    fullAddress: "1 Main St, Unit 5, Boston, MA 02109",
    street: "1 Main St",
    suite: "Unit 5",
    city: "Boston",
    state: "MA",
    zip: "02109",
    latitude: 42.3601,
    longitude: -71.0589,
    timezone: "America/New_York",
  },
  {
    id: "addr-8",
    fullAddress: "2000 W Loop S, Ste 400, Houston, TX 77027",
    street: "2000 W Loop S",
    suite: "Ste 400",
    city: "Houston",
    state: "TX",
    zip: "77027",
    latitude: 29.7604,
    longitude: -95.3698,
    timezone: "America/Chicago",
  },
];

export function filterMockPhysicalAddresses(
  query: string
): MockPhysicalAddress[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return [...mockPhysicalAddresses];
  }
  return mockPhysicalAddresses.filter((a) => {
    const hay = [a.fullAddress, a.street, a.suite, a.city, a.state, a.zip]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
